import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'quiet'
export type ButtonSize = 'md' | 'sm'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Three, and there is no fourth. A screen that wants one is wrong. */
  variant?: ButtonVariant
  /** `md` is drawn 44px tall, `sm` 34px — both inside a 44px tap target. */
  size?: ButtonSize
  /** A Lucide glyph before the label. Sized by the caller: 16 with `sm`, 20 with `md`. */
  icon?: ReactNode
  block?: boolean
  /** Working. Shows a spinner in place of the icon and stops the button
   *  answering — a busy button that can be pressed again is a double submit.
   *  Kept separate from `disabled` because they mean different things to a
   *  screen reader: `aria-busy` says "wait", `disabled` says "not for you". */
  busy?: boolean
  children: ReactNode
}

/**
 * The one button.
 *
 * The face is a span rather than the button's own box because the small size
 * has to be *drawn* 34px tall and *hit* 44px tall, which one box cannot be.
 * The button is the 44px tap target — it has no paint of its own — and the
 * face inside it is what you see. Every size goes through the same two
 * elements so there is one anatomy to style rather than two.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, block = false, busy = false,
    className = '', disabled, children, ...rest },
  ref,
) {
  const classes = [
    'u-btn', `u-btn--${variant}`, `u-btn--${size}`,
    block ? 'u-btn--block' : '', busy ? 'is-busy' : '', className,
  ].filter(Boolean).join(' ')

  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      <span className="u-btn__face">
        {busy ? <span className="u-spinner" aria-hidden="true" /> : icon}
        <span className="u-btn__label">{children}</span>
      </span>
    </button>
  )
})
